import { safeParseSavedRun, safeParseScenarioDocument, type SavedRun, type ScenarioDocument } from './model.js';
import { RESOURCE_LIMITS } from '../config.js';

export interface WorkspaceState {
  readonly projects: readonly ScenarioDocument[];
  readonly runs: readonly SavedRun[];
}

const DB_NAME = 'aerocontrol-studio';
const STORE = 'workspace';
const KEY = 'current';
const FALLBACK_KEY = 'aerocontrol-studio/workspace/v1';

export function sanitiseWorkspace(value: unknown): WorkspaceState | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const candidate = value as { readonly projects?: unknown; readonly runs?: unknown };
  const projects = Array.isArray(candidate.projects)
    ? candidate.projects.flatMap((item) => { const parsed = safeParseScenarioDocument(item); return parsed.success ? [parsed.data] : []; })
    : [];
  const runs = Array.isArray(candidate.runs)
    ? candidate.runs.flatMap((item) => { const parsed = safeParseSavedRun(item); return parsed.success ? [parsed.data] : []; }).slice(-RESOURCE_LIMITS.retainedRuns)
    : [];
  return { projects, runs };
}

function localLoad(): WorkspaceState | null {
  try {
    const raw = localStorage.getItem(FALLBACK_KEY);
    return raw ? sanitiseWorkspace(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function localSave(state: WorkspaceState): void {
  localStorage.setItem(FALLBACK_KEY, JSON.stringify(state));
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in globalThis)) return reject(new Error('IndexedDB is unavailable.'));
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB could not be opened.'));
  });
}

export async function loadWorkspace(): Promise<{ state: WorkspaceState | null; storage: 'indexeddb' | 'localstorage' | 'memory' }> {
  try {
    const db = await openDatabase();
    const state = await new Promise<WorkspaceState | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const request = tx.objectStore(STORE).get(KEY);
      request.onsuccess = () => resolve(sanitiseWorkspace(request.result));
      request.onerror = () => reject(request.error ?? new Error('Workspace read failed.'));
    });
    db.close();
    return { state, storage: 'indexeddb' };
  } catch {
    const state = localLoad();
    return { state, storage: state ? 'localstorage' : 'memory' };
  }
}

export async function saveWorkspace(state: WorkspaceState): Promise<'indexeddb' | 'localstorage'> {
  if (state.runs.length > RESOURCE_LIMITS.retainedRuns) {
    throw new RangeError(`Workspace contains ${state.runs.length} runs, exceeding the disclosed local retention limit ${RESOURCE_LIMITS.retainedRuns}.`);
  }
  for (const project of state.projects) {
    if (!safeParseScenarioDocument(project).success) throw new RangeError('Workspace contains an invalid project and was not saved.');
  }
  for (const run of state.runs) {
    if (!safeParseSavedRun(run).success) throw new RangeError('Workspace contains an invalid run and was not saved.');
  }
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(state, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Workspace write failed.'));
      tx.onabort = () => reject(tx.error ?? new Error('Workspace write aborted.'));
    });
    db.close();
    return 'indexeddb';
  } catch {
    localSave(state);
    return 'localstorage';
  }
}

export async function clearWorkspace(): Promise<void> {
  localStorage.removeItem(FALLBACK_KEY);
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Workspace clear failed.'));
    });
    db.close();
  } catch {
    // Local fallback is already cleared. The UI reports the selected storage adapter separately.
  }
}
